import React, { useState } from 'react';
import { useConversationsNotesDialogStyles } from './ConversationNotesDialog.styles';
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    Card,
    CardHeader,
    Field,
    Spinner,
    Text,
    Textarea,
    Tooltip,
} from '@fluentui/react-components';
import {
    Delete16Regular,
    Dismiss24Regular,
    Person16Regular,
} from '@fluentui/react-icons';
import { ConversationNotesResponse } from '../../../model/brokerConnections/ConversationNotesResponse';
import { useTranslation } from 'react-i18next';
import { activeConversationLengths } from '../../../store/features/activeConversations/activeConversationsAPI';

interface ConversationNotesDialogProps {
    open: boolean;
    embedded?: boolean;
    notes: ConversationNotesResponse[];
    loading: boolean;
    error: string | null;
    currentUserId?: string;
    flightDetails?: {
        flightNumber: string;
        departureAirportIata: string;
        arrivalAirportIata: string;
        acReg: string;
    };
    onClose: () => void;
    onSubmit: (note: string) => Promise<void>;
    onDelete: (noteId: string) => Promise<void>;
}

export const ConversationNotesDialog: React.FC<
    ConversationNotesDialogProps
> = ({
    open,
    embedded = false,
    notes,
    loading,
    error,
    currentUserId,
    flightDetails,
    onClose,
    onSubmit,
    onDelete,
}) => {
    const { t } = useTranslation();
    const [noteDraft, setNoteDraft] = useState('');
    const styles = useConversationsNotesDialogStyles();

    const handleClose = () => {
        setNoteDraft('');
        onClose();
    };

    const [submitting, setSubmitting] = useState(false);
    const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null);
    const normalizedCurrentUserId = currentUserId?.trim().toLowerCase();

    const handleSubmit = async () => {
        const note = noteDraft.trim();
        if (!note || submitting) return;

        setSubmitting(true);
        try {
            await onSubmit(note);
            setNoteDraft('');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (noteId: string) => {
        if (deletingNoteId) return;

        setDeletingNoteId(noteId);
        try {
            await onDelete(noteId);
        } finally {
            setDeletingNoteId(null);
        }
    };

    const dialogContent = (
        <>
                    <DialogTitle
                        action={
                            embedded ? undefined : (
                            <Button
                                appearance="subtle"
                                aria-label="Close conversation notes"
                                icon={<Dismiss24Regular />}
                                onClick={handleClose}
                            />
                            )
                        }
                    >
                        {flightDetails
                            ? `${t('ActiveConversations_ConversationNotes')} - BA${flightDetails.flightNumber} · ${flightDetails.departureAirportIata} → ${flightDetails.arrivalAirportIata} · ${flightDetails.acReg}`
                            : t('ActiveConversations_ConversationNotes')}
                    </DialogTitle>
                    <DialogContent className={styles.content}>
                        {loading ? (
                            <Spinner
                                size="small"
                                label="Loading conversation notes"
                            />
                        ) : error ? (
                            <div role="alert">{error}</div>
                        ) : notes.length === 0 ? (
                            <div className={styles.emptyState}>
                                <Text>
                                    {t(
                                        'ActiveConversations_NoConversationNotes'
                                    )}
                                </Text>
                            </div>
                        ) : (
                            <ul className={styles.notesList}>
                                {notes.map((note) => (
                                    <li key={note.id}>
                                        <Card className={styles.noteCard}>
                                            <CardHeader
                                                header={
                                                    <div
                                                        className={
                                                            styles.noteHeader
                                                        }
                                                    >
                                                        <Person16Regular />
                                                        <Text size={200}>
                                                            {note.createdBy}
                                                        </Text>
                                                        <Text size={200}>
                                                            ·
                                                        </Text>
                                                        <Text size={200}>
                                                            {new Date(
                                                                note.createdOnUtc
                                                            ).toLocaleString()}
                                                        </Text>
                                                        {normalizedCurrentUserId &&
                                                            note.createdByUserId
                                                                ?.trim()
                                                                .toLowerCase() ===
                                                                normalizedCurrentUserId && (
                                                                <Tooltip
                                                                    content={t(
                                                                        'ActiveConversations_DeleteNote',
                                                                        {
                                                                            defaultValue:
                                                                                'Delete note',
                                                                        }
                                                                    )}
                                                                    relationship="label"
                                                                >
                                                                    <Button
                                                                        appearance="primary"
                                                                        aria-label={t(
                                                                            'ActiveConversations_DeleteNote',
                                                                            {
                                                                                defaultValue:
                                                                                    'Delete note',
                                                                            }
                                                                        )}
                                                                        icon={
                                                                            <Delete16Regular />
                                                                        }
                                                                        size="small"
                                                                        disabled={
                                                                            deletingNoteId !==
                                                                            null
                                                                        }
                                                                        onClick={() =>
                                                                            handleDelete(
                                                                                note.id
                                                                            )
                                                                        }
                                                                    />
                                                                </Tooltip>
                                                            )}
                                                    </div>
                                                }
                                            />
                                            <Text className={styles.noteBody}>
                                                {note.note}
                                            </Text>
                                        </Card>
                                    </li>
                                ))}
                            </ul>
                        )}
                        <div className={styles.addNote}>
                            <Field
                                label={t('ActiveConversations_AddNote', {
                                    defaultValue: 'Add a note',
                                })}
                            >
                                <Textarea
                                    value={noteDraft}
                                    onChange={(_, data) =>
                                        setNoteDraft(data.value)
                                    }
                                    placeholder={t(
                                        'ActiveConversations_AddNotePlaceholder',
                                        {
                                            defaultValue:
                                                'Enter a note for this conversation',
                                        }
                                    )}
                                    resize="vertical"
                                    rows={5}
                                    maxLength={
                                        activeConversationLengths.note.max
                                    }
                                />
                                <Text
                                    className={styles.characterCount}
                                    size={200}
                                >
                                    {noteDraft.length}/
                                    {activeConversationLengths.note.max}
                                </Text>
                            </Field>
                        </div>
                    </DialogContent>
                    <DialogActions className={styles.actions}>
                        <Button
                            appearance="primary"
                            onClick={handleSubmit}
                            disabled={!noteDraft.trim() || submitting}
                        >
                            {t('Submit')}
                        </Button>
                    </DialogActions>
        </>
    );

    if (embedded) {
        return <div className={styles.embedded}>{dialogContent}</div>;
    }

    return (
        <Dialog
            open={open}
            onOpenChange={(_, data) => !data.open && handleClose()}
        >
            <DialogSurface>
                <DialogBody>{dialogContent}</DialogBody>
            </DialogSurface>
        </Dialog>
    );
};
